import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let refreshTokenInterval = null;

    // Check for redirect result on mount
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          const token = await result.user.getIdToken();
          const userData = {
            uid: result.user.uid,
            email: result.user.email,
            name: result.user.displayName || result.user.email?.split('@')[0],
            token,
          };
          setUser(userData);
          localStorage.setItem('firebaseToken', token);
        }
      } catch (error) {
      }
    };
    
    checkRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clear any existing refresh interval
      if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
        refreshTokenInterval = null;
      }

      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
          token,
        };
        setUser(userData);
        // Store token for API requests
        localStorage.setItem('firebaseToken', token);
        
        // Set up automatic token refresh (Firebase tokens expire after ~1 hour)
        refreshTokenInterval = setInterval(async () => {
          try {
            const freshToken = await firebaseUser.getIdToken(true); // Force refresh
            localStorage.setItem('firebaseToken', freshToken);
            // Update user data with new token
            setUser(prev => ({
              ...prev,
              token: freshToken,
            }));
          } catch (error) {
          }
        }, 50 * 60 * 1000); // Refresh every 50 minutes
      } else {
        setUser(null);
        localStorage.removeItem('firebaseToken');
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
      }
    };
  }, []);

  const login = async (useRedirect = false) => {
    const provider = new GoogleAuthProvider();
    
    // Check for redirect result first (in case user was redirected back)
    try {
      const redirectResult = await getRedirectResult(auth);
      if (redirectResult) {
        const token = await redirectResult.user.getIdToken();
        return {
          uid: redirectResult.user.uid,
          email: redirectResult.user.email,
          name: redirectResult.user.displayName || redirectResult.user.email?.split('@')[0],
          token,
        };
      }
    } catch (error) {
    }
    
    try {
      if (useRedirect) {
        // Use redirect method (better for mobile and when popups are blocked)
        await signInWithRedirect(auth, provider);
        // Note: This will redirect the page, so we won't return here
        // The result will be handled by getRedirectResult on the next page load
        return null;
      } else {
        // Try popup first
        const result = await signInWithPopup(auth, provider);
        const token = await result.user.getIdToken();
        return {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || result.user.email?.split('@')[0],
          token,
        };
      }
    } catch (error) {
      
      // Handle specific error codes
      if (error.code === 'auth/cancelled-popup-request') {
        // User closed the popup - not really an error, just return null
        return null;
      } else if (error.code === 'auth/popup-blocked') {
        // Popup was blocked - suggest using redirect
        throw new Error('Popup blocked. Please allow popups or use the redirect login method.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup
        return null;
      }
      
      // For other errors, throw them
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
